import { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Textarea,
  IconButton,
  Flex,
  Box,
  Text,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { GoPlus } from 'react-icons/go';
import { MdDeleteOutline } from 'react-icons/md';
import axios from '../../utils/axios';

const AutomatedMessagesDialog = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState(['']);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  // ── Fetch existing messages when dialog opens ──
  useEffect(() => {
    if (!isOpen) return;

    const fetchMessages = async () => {
      try {
        setFetching(true);
        const res = await axios.get('/api/threads/get-automated-threads');
        const saved = res.data?.data?.automationThreads;

        // If saved messages exist load them, otherwise default to 1 empty textarea
        setMessages(saved?.length > 0 ? saved : ['']);
      } catch (error) {
        console.error('Failed to fetch messages:', error.message);
        setMessages(['']);
      } finally {
        setFetching(false);
      }
    };

    fetchMessages();
  }, [isOpen]); // runs every time dialog opens

  const handleAdd = () => setMessages((prev) => [...prev, '']);

  const handleChange = (index, value) => {
    setMessages((prev) => prev.map((msg, i) => (i === index ? value : msg)));
  };

  const handleRemove = (index) => {
    if (messages.length === 1) return;
    setMessages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const filtered = messages.filter((m) => m.trim() !== '');
    if (filtered.length === 0) return;

    try {
      setLoading(true);
      await axios.post('/api/threads/automated-thread', {
        automationThreads: filtered,
      });
      onClose();
    } catch (error) {
      console.error('Failed to save messages:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMessages(['']);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px" fontWeight={500}>
          Automated Messages
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Text fontSize="13px" color="gray.500" mb={4}>
            Add messages to send in sequence. Click + to add more.
          </Text>

          {/* Loading state while fetching */}
          {fetching ? (
            <Center py={8}>
              <Spinner size="sm" />
            </Center>
          ) : (
            <Flex direction="column" gap={3}>
              {messages.map((msg, index) => (
                <Box key={index}>
                  <Text fontSize="12px" color="gray.400" mb={1}>
                    Message {index + 1}
                  </Text>
                  <Flex gap={2} align="flex-start">
                    <Textarea
                      value={msg}
                      onChange={(e) => handleChange(index, e.target.value)}
                      placeholder={`Type message ${index + 1}...`}
                      resize="none"
                      rows={3}
                      fontSize="14px"
                      _focus={{
                        boxShadow: 'none',
                        border: 'solid 1px #854DFF',
                      }}
                    />
                    <IconButton
                      aria-label="Remove message"
                      icon={<MdDeleteOutline />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      isDisabled={messages.length === 1}
                      onClick={() => handleRemove(index)}
                      mt={1}
                    />
                  </Flex>
                </Box>
              ))}

              <Button
                leftIcon={<GoPlus />}
                size="sm"
                variant="ghost"
                mt={2}
                onClick={handleAdd}
                color="#854DFF"
                _hover={{ background: 'purple.50' }}
                alignSelf="flex-start"
              >
                Add message
              </Button>
            </Flex>
          )}
        </ModalBody>

        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            background="#854DFF"
            color="white"
            _hover={{ background: '#7440e8' }}
            onClick={handleSave}
            isLoading={loading}
            loadingText="Saving..."
            isDisabled={fetching || messages.every((m) => m.trim() === '')}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AutomatedMessagesDialog;
